import { useMemo } from 'react';
import { resolveMediaUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import { labelClass } from '@/components/ui/inputStyles';
import { buildSiteHierarchyGroups, type SiteTreeMeta } from '@/lib/catalogSiteTree';

export type SiteFloatActionsOverride = {
  publish_qr_path?: string;
  promo_qr_path?: string;
};

type Props = {
  sites: SiteTreeMeta[];
  siteIds: number[];
  pages: Record<string, SiteFloatActionsOverride>;
  onPatch: (siteId: number, patch: Partial<SiteFloatActionsOverride>) => void;
  onClear: (siteId: number) => void;
  onUpload: (siteId: number, kind: 'publish' | 'promo', file: File) => void | Promise<void>;
};

const detailsClass =
  'rounded-lg border border-slate-200 bg-white shadow-sm open:ring-1 open:ring-slate-200/80 [&_summary::-webkit-details-marker]:hidden';
const summaryClass =
  'flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-slate-50/80';

function hasOverride(pages: Record<string, SiteFloatActionsOverride>, siteId: number): boolean {
  return Object.prototype.hasOwnProperty.call(pages, String(siteId));
}

function QrPair({
  o,
  siteId,
  onPatch,
  onUpload,
}: {
  siteId: number;
  o: SiteFloatActionsOverride;
  onPatch: (p: Partial<SiteFloatActionsOverride>) => void;
  onUpload: (kind: 'publish' | 'promo', file: File) => void | Promise<void>;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {(['publish', 'promo'] as const).map((kind) => {
        const path = kind === 'publish' ? o.publish_qr_path : o.promo_qr_path;
        const title = kind === 'publish' ? '我要发布' : '我要推广';
        return (
          <div key={kind}>
            <p className="mb-2 text-xs font-medium text-slate-600">{title} · 微信二维码</p>
            <div className="flex flex-wrap items-center gap-2">
              {path ? (
                <img src={resolveMediaUrl(path)} alt="" className="h-20 w-20 rounded-lg border object-cover" />
              ) : (
                <span className="text-xs text-slate-500">未上传（沿用上级）</span>
              )}
              <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs hover:bg-white">
                上传
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void onUpload(kind, f);
                  }}
                />
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SiteFloatActionsEditor({ sites, siteIds, pages, onPatch, onClear, onUpload }: Props) {
  const hierarchyGroups = useMemo(() => buildSiteHierarchyGroups(sites.filter((s) => siteIds.includes(s.id))), [sites, siteIds]);

  function renderSite(siteId: number, siteName: string, depth: 0 | 1, parentName?: string) {
    const inheriting = depth === 1 && !hasOverride(pages, siteId);
    const override = hasOverride(pages, siteId);
    const o = pages[String(siteId)] || {};

    return (
      <details key={siteId} className={cn(detailsClass, depth === 1 && 'border-l-4 border-l-orange-500/35')}>
        <summary className={summaryClass}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <SiteLevelBadge depth={depth} className={depth === 1 ? '!px-1 !py-px !text-[9px]' : undefined} />
            <span className="truncate font-medium text-slate-900">{siteName}</span>
            {depth === 1 ? (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  inheriting ? 'bg-slate-100 text-slate-600' : 'bg-orange-50 text-orange-900 ring-1 ring-orange-200/80',
                )}
              >
                {inheriting ? '沿用一级' : '已单独配置'}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
        </summary>
        <div className="border-t border-slate-100 px-3 pb-3 pt-2">
          {depth === 1 && inheriting && parentName ? (
            <p className="mb-2 rounded-lg border border-orange-200/80 bg-orange-50/90 px-3 py-2 text-xs text-orange-950">
              当前沿用一级「{parentName}」。上传或修改后将保存为本站配置。
            </p>
          ) : null}
          {override ? (
            <div className="mb-2 flex justify-end">
              <Button type="button" variant="ghost" size="sm" className="!text-teal-800" onClick={() => onClear(siteId)}>
                {depth === 1 ? '恢复沿用一级' : '恢复沿用全站默认'}
              </Button>
            </div>
          ) : null}
          <QrPair
            siteId={siteId}
            o={o}
            onPatch={(p) => onPatch(siteId, p)}
            onUpload={(kind, f) => onUpload(siteId, kind, f)}
          />
        </div>
      </details>
    );
  }

  if (siteIds.length === 0) return null;

  return (
    <div className="mt-4 space-y-3 rounded-lg border border-dashed border-orange-200/80 bg-orange-50/30 p-3">
      <p className="text-xs font-medium text-slate-700">按站点覆盖悬浮钮二维码（二级默认沿用一级）</p>
      {hierarchyGroups.map((g, gi) => {
        const parent = g.parent?.site;
        const parentId = parent?.id ?? 0;
        const parentName = parent?.site_name ?? '';
        if (parent && parentId > 0 && g.children.length > 0) {
          return (
            <details key={`fa-${gi}-${parentId}`} className={detailsClass}>
              <summary className={cn(summaryClass, 'bg-slate-50/60')}>
                <span className="flex items-center gap-2">
                  <SiteLevelBadge depth={0} />
                  <span className="font-semibold text-slate-900">{parent.site_name}</span>
                  <span className="text-[10px] text-slate-500">含 {g.children.length} 个二级</span>
                </span>
                <span className="text-xs text-teal-700">展开</span>
              </summary>
              <div className="space-y-3 border-t border-slate-100 p-3">
                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-700">一级站点</p>
                  <QrPair
                    siteId={parentId}
                    o={pages[String(parentId)] || {}}
                    onPatch={(p) => onPatch(parentId, p)}
                    onUpload={(kind, f) => onUpload(parentId, kind, f)}
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">二级站点</p>
                  {g.children.map((c) => renderSite(c.site.id, c.site.site_name, 1, parentName))}
                </div>
              </div>
            </details>
          );
        }
        if (parent) return renderSite(parentId, parent.site_name, 0);
        return (
          <div key={`fa-o-${gi}`} className="space-y-2">
            {g.children.map((c) => renderSite(c.site.id, c.site.site_name, 1))}
          </div>
        );
      })}
    </div>
  );
}
